import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  ButtonComponent,
  DialogRefService,
  PlainButtonComponent,
  TooltipTriggerDirective,
} from '@group24ag/flowkit';
import { ColorThemeService } from '@core/theming/color-theme.service';
import {
  COLOR_PALETTES,
  COLOR_SLOTS,
  ColorPaletteName,
  ColorSlotKey,
} from '@core/theming/color-slots';

/**
 * Lets the user recolor the application. Every change applies immediately and is
 * persisted right away, so the dialog needs no save step — only a way back to
 * the defaults.
 **/
@Component({
  selector: 'app-color-theme-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, PlainButtonComponent, TooltipTriggerDirective],
  template: `
    <div class="base--bg flex size-full max-h-full flex-col overflow-hidden">
      <div
        class="content--bg flex h-full min-h-0 flex-col gap-y-4 overflow-y-auto px-5 py-4">
        @for (slot of slots; track slot.key) {
          <section
            class="element--border element--contrast-bg rounded-xl border p-4">
            <div class="flex items-start justify-between gap-x-4">
              <div class="min-w-0">
                <h3 class="text--contrast-high text-sm font-semibold">
                  {{ slot.label }}
                </h3>

                <p class="text--contrast-lowest mt-0.5 text-xs">
                  {{ slot.hint }}
                </p>
              </div>

              @if (isOverridden(slot.key)) {
                <button flkPlainButton (click)="reset(slot.key)">Reset</button>
              }
            </div>

            <div class="mt-4 flex flex-wrap items-center gap-3">
              @for (palette of palettes; track palette) {
                <button
                  flkTooltipTrigger
                  type="button"
                  class="size-8 cursor-pointer rounded-full ring-offset-2 transition"
                  [class.ring-2]="activePalettes()[slot.key] === palette"
                  [class.ring-dune-500]="activePalettes()[slot.key] === palette"
                  [style.background-color]="paletteColors()[palette]"
                  [tooltip]="palette"
                  [position]="'above'"
                  (click)="select(slot.key, palette)"></button>
              }

              <span class="element--border mx-1 h-6 border-l"></span>

              <label
                flkTooltipTrigger
                class="relative size-8 cursor-pointer overflow-hidden rounded-full"
                [style.background-color]="resolvedColors()[slot.key]"
                [class.ring-2]="isCustom(slot.key)"
                [class.ring-dune-500]="isCustom(slot.key)"
                [class.ring-offset-2]="isCustom(slot.key)"
                [tooltip]="'Custom color'"
                [position]="'above'">
                <input
                  type="color"
                  class="absolute inset-0 size-full cursor-pointer opacity-0"
                  [value]="resolvedColors()[slot.key]"
                  (input)="selectCustom(slot.key, $event)" />
              </label>
            </div>
          </section>
        }
      </div>

      <div
        class="element--contrast-bg element--border flex w-full items-center justify-between gap-x-3 border-t px-5 py-3">
        <span class="text--contrast-lowest text-sm">
          Saved in this browser only.
        </span>

        <div class="flex gap-x-3 px-0.5">
          <button
            flkPlainButton
            [disabled]="!customized()"
            (click)="resetAll()">
            Reset all
          </button>

          <button
            flkButton
            appearance="primary"
            variant="accent"
            (click)="close()">
            Done
          </button>
        </div>
      </div>
    </div>
  `,
})
export class ColorThemeDialogComponent {
  /** INJECTIONS **/
  /****/
  private readonly colorTheme = inject(ColorThemeService);

  /** Holds the reference to the dialog ref **/
  private readonly dialogRef = inject(DialogRefService);

  /** PROPERTIES **/
  /****/
  protected readonly slots = COLOR_SLOTS;

  /****/
  protected readonly palettes = COLOR_PALETTES;

  /****/
  protected readonly customized = this.colorTheme.customized;

  /****/
  protected readonly paletteColors = this.colorTheme.paletteColors;

  /****/
  protected readonly resolvedColors = this.colorTheme.resolvedColors;

  /****/
  protected readonly activePalettes = this.colorTheme.activePalettes;

  /** Whether the slot uses a freely picked color. **/
  protected isCustom(key: ColorSlotKey): boolean {
    return this.colorTheme.choiceOf(key)?.kind === 'custom';
  }

  /** Whether the slot deviates from the stylesheet default. **/
  protected isOverridden(key: ColorSlotKey): boolean {
    return !!this.colorTheme.choiceOf(key);
  }

  protected select(key: ColorSlotKey, palette: ColorPaletteName): void {
    this.colorTheme.setPalette(key, palette);
  }

  protected selectCustom(key: ColorSlotKey, event: Event): void {
    this.colorTheme.setCustom(key, (event.target as HTMLInputElement).value);
  }

  protected reset(key: ColorSlotKey): void {
    this.colorTheme.reset(key);
  }

  protected resetAll(): void {
    this.colorTheme.resetAll();
  }

  protected close(): void {
    this.dialogRef.close(true);
  }
}
